import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData, updateData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Box, Typography, IconButton, Tooltip, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  TextField, InputAdornment, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Refresh, Close, Search,
} from '@mui/icons-material';

/* ═══════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════ */
const T = {
  bg: '#f0f4f8',
  card: '#ffffff',
  border: '#e2e8f0',
  text: '#1e293b',
  textSec: '#475569',
  muted: '#94a3b8',
  blue: '#1e40af',
  green: '#16a34a',
  red: '#dc2626',
  amber: '#d97706',
  teal: '#0f766e',
  purple: '#7c3aed',
  radius: 10,
  shadow: '0 1px 4px rgba(0,0,0,0.08)',
  shadowLg: '0 4px 16px rgba(0,0,0,0.12)',
};

const fmt = n => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = d => { if (!d) return '-'; const dt = new Date(d); return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`; };

/* ═══════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════ */
const MetricCard = ({ label, value, sub, color, icon, onClick }) => (
  <div
    onClick={onClick}
    style={{
      flex: '1 1 150px', minWidth: 150, position: 'relative', overflow: 'hidden',
      background: T.card, borderRadius: T.radius, padding: '14px 16px',
      border: `1px solid ${T.border}`, boxShadow: T.shadow,
      borderTop: `3px solid ${color}`,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.2s',
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = T.shadowLg; }}
    onMouseLeave={e => { if (onClick) e.currentTarget.style.boxShadow = T.shadow; }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.text, lineHeight: 1, letterSpacing: '-0.5px' }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color, marginTop: 6, fontWeight: 600 }}>{sub}</div>}
      </div>
      {icon && (
        <div style={{ width: 36, height: 36, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </div>
      )}
    </div>
  </div>
);

const SectionTitle = ({ children, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
    <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{children}</span>
    {right && <span style={{ fontSize: 11, color: T.muted }}>{right}</span>}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    'Delivered': { bg: '#dcfce7', color: '#16a34a', label: 'Delivered' },
    'Pending': { bg: '#fef9c3', color: '#ca8a04', label: 'Pending' },
    'OnSite': { bg: '#dbeafe', color: '#1d4ed8', label: 'On Site' },
    'COD': { bg: '#fce7f3', color: '#be185d', label: 'COD' },
    'Exchange': { bg: '#f3e8ff', color: '#7c3aed', label: 'Exchange' },
  };
  const s = map[status] || { bg: '#f1f5f9', color: T.muted, label: status || '-' };
  return (
    <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
const DriverDashboard = () => {
  useSessionValidation();
  const { companyId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: COD List, 1: On-Site List
  const [codSales, setCodSales] = useState([]);
  const [onsiteSales, setOnsiteSales] = useState([]);
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  /* ── COD Inputs State ── */
  const [isReceived, setIsReceived] = useState(false);
  const [isExchangedCompleted, setIsExchangedCompleted] = useState(false);

  const [PaymentMode, setPaymentType] = useState('Cash'); // Default 'Cash'
  const [upiRef, setUpiRef] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [bankName, setBankName] = useState('');
  const [driverRemarks, setDriverRemarks] = useState('');
  const [driver1Remarks, setDriver1Remarks] = useState('');

  /* ── Track original values to detect edits ── */
  const [origPaymentMode, setOrigPaymentMode] = useState('Cash');
  const [origDriverRemarks, setOrigDriverRemarks] = useState('');
  const [origDriver1Remarks, setOrigDriver1Remarks] = useState('');

  /* ── Payment mode options ── */
  const PAYMENT_MODES = [
    { value: 'Cash',       label: 'Cash',        icon: '💵', color: '#16a34a', bg: '#dcfce7' },
    { value: 'UPI',        label: 'UPI / QR',    icon: '📲', color: '#7c3aed', bg: '#f3e8ff' },
    { value: 'Cheque',     label: 'Cheque',      icon: '🏦', color: '#1d4ed8', bg: '#dbeafe' },
  ];

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
 
  /* ── Fetch Data ── */
  const fetchAllData = useCallback(async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const [codRes, onsiteRes] = await Promise.allSettled([
        fetchData('ExecutivedashboardApi', `/DriverAllocatedCODList/${companyId}/${userId}`, token),
        fetchData('ExecutivedashboardApi', `/DriverAllocatedOnsiteList/${companyId}/${userId}`, token),
      ]);

      if (codRes.status === 'fulfilled') {
        const codData = codRes.value;
        const list = Array.isArray(codData?.response_data) ? codData.response_data
          : Array.isArray(codData) ? codData : [];
        setCodSales(list);
      }

      if (onsiteRes.status === 'fulfilled') {
        const onsiteData = onsiteRes.value;
        const list = Array.isArray(onsiteData?.response_data) ? onsiteData.response_data
          : Array.isArray(onsiteData) ? onsiteData : [];
        setOnsiteSales(list);
      }
    } catch {
      setSnack({ open: true, msg: 'Error loading delivery data', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, token]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  /* ── Open Details Dialog ── */
  const handleOpenDetail = (sale) => {
    setSelectedSale(sale);
    setIsReceived(Boolean(sale.IsReceived));
    setPaymentType(sale.PaymentMode || 'Cash');
    setUpiRef(sale.UpiRef || '');
    setChequeNo(sale.ChequeNo || '');
    setBankName(sale.BankName || '');
    setDriverRemarks(sale.Remarks || '');
    setIsExchangedCompleted(Boolean(sale.IsExchangedCompleted));
    setDriver1Remarks(sale.Remarks || '');
    /* store originals for change detection */
    setOrigPaymentMode(sale.PaymentMode || 'Cash');
    setOrigDriverRemarks(sale.Remarks || '');
    setOrigDriver1Remarks(sale.Remarks || '');
    setDetailOpen(true);
  };

  const getBalance = (s) => parseFloat(s.ToCollect ?? s.BalanceAmount ?? 0);

  /* ── Search Filter ── */
  const activeList = useMemo(() => {
    const rawList = activeTab === 0 ? codSales : onsiteSales;
    if (!search.trim()) return rawList;

    const query = search.toLowerCase();
    return rawList.filter(s => {
      const billNo = (s.BillNo || `${s.BillPrefix || ''}${s.BillNumber || ''}`).toLowerCase();
      const customer = (s.CustomerName || '').toLowerCase();
      const address = `${s.ShipToAddress1 || ''} ${s.ShipToLocation || ''} ${s.ShipTo || ''}`.toLowerCase();
      const vehicle = (s.VehicleNumber || '').toLowerCase();

      return billNo.includes(query) || customer.includes(query) || address.includes(query) || vehicle.includes(query);
    });
  }, [activeTab, codSales, onsiteSales, search]);

  /* ── Submit COD Handler ── */
  const handleSubmitCOD = async () => {
    if (!isReceived) {
      setSnack({ open: true, msg: 'Please tick "Amount Received" before submitting', sev: 'warning' });
      return;
    }

    setSubmitting(true);
    try {

      const payload = {
        ...selectedSale,
        CompanyId: companyId,
        UserId: userId,
        Id: selectedSale.Id,
        IsReceived: isReceived,
        PaymentMode: PaymentMode,
        UpiRef: PaymentMode === 'UPI' ? upiRef : '',
        ChequeNo: PaymentMode === 'Cheque' ? chequeNo : '',
        BankName: PaymentMode === 'Cheque' ? bankName : '',
        Remarks: driverRemarks,
      };
      await updateData('MasterTypeApi', selectedSale.Id, payload, token);
      setSnack({ open: true, msg: 'Payment status updated successfully!', sev: 'success' });
      setDetailOpen(false);
      fetchAllData();
    } catch (err) {
      setSnack({ open: true, msg: 'Failed to update payment status', sev: 'error' });
    } finally {
      setSubmitting(false);
    }
  };


  /* ── Update COD Handler (PaymentMode / Remarks change on already-received bills) ── */
  const handleUpdateCOD = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...selectedSale,
        CompanyId: companyId,
        UserId: userId,
        Id: selectedSale.Id,
        IsReceived: true,
        PaymentMode: PaymentMode,
        Remarks: driverRemarks,
      };
      await updateData('MasterTypeApi', selectedSale.Id, payload, token);
      setOrigPaymentMode(PaymentMode);
      setOrigDriverRemarks(driverRemarks);
      setSnack({ open: true, msg: 'Payment details updated successfully!', sev: 'success' });
      setDetailOpen(false);
      fetchAllData();
    } catch {
      setSnack({ open: true, msg: 'Failed to update payment details', sev: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Submit OnSite Handler ── */
  const handleSubmitOnSite = async () => {
    if (!isExchangedCompleted) {
      setSnack({ open: true, msg: 'Please tick "Exchange Item PickUp" before submitting', sev: 'warning' });
      return;
    }

    setSubmitting(true);
    try {

      // API Payload:
      const payload = {
        ...selectedSale,
        CompanyId: companyId,
        Id: selectedSale.Id,
        IsExchangedCompleted: isExchangedCompleted,    // bool (true)
        Remarks: driver1Remarks,
        ExchangeUpdatedBy : userId
      };
      await updateData('UOMApi', selectedSale.Id, payload, token);
      setSnack({ open: true, msg: 'updated successfully!', sev: 'success' });
      setDetailOpen(false);
      fetchAllData();
    } catch (err) {
      setSnack({ open: true, msg: 'Failed to update', sev: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const map = {
      'Delivered': { bg: '#dcfce7', color: '#16a34a', label: 'Delivered' },
      'Completed Pickup': { bg: '#dcfce7', color: '#16a34a', label: 'Completed Pickup' }, // <-- പുതിയ സ്റ്റാറ്റസ്
      'Pending': { bg: '#fef9c3', color: '#ca8a04', label: 'Pending' },
      'OnSite': { bg: '#dbeafe', color: '#1d4ed8', label: 'On Site' },
      'COD': { bg: '#fce7f3', color: '#be185d', label: 'COD' },
      'Exchange': { bg: '#f3e8ff', color: '#7c3aed', label: 'Exchange' },
    };
    const s = map[status] || { bg: '#f1f5f9', color: T.muted, label: status || '-' };
    return (
      <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
        {s.label}
      </span>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', background: T.bg, p: { xs: 1.5, sm: 2.5 } }}>
      <LogoLoader open={loading || submitting} message={submitting ? "Updating payment status..." : "Loading deliveries..."} />

      {/* ── Tabs, Search & Refresh ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${T.border}`, px: 1, py: 0.5 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700, fontSize: 13, px: 2 }
            }}
          >
            <Tab label={`💵 COD List (${codSales.length})`} />
            <Tab label={`📍 On-Site List (${onsiteSales.length})`} />
          </Tabs>
        </Paper>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            size="small"
            placeholder="Search bill, customer, vehicle..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: { xs: '100%', sm: 280 }, background: '#fff', borderRadius: 1 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: T.muted }} /></InputAdornment>
            }}
          />
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchAllData} sx={{ background: T.card, border: `1px solid ${T.border}` }}>
              <Refresh sx={{ color: T.blue }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Delivery Table ── */}
      <div style={{ background: T.card, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
          <SectionTitle right={`${activeList.length} records`}>
            {activeTab === 0 ? '📋 COD Delivery List' : '📋 On-Site Delivery List'}
          </SectionTitle>
        </div>

        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['#', 'Bill No', 'Allocated Date', 'Customer', 'Delivery Address', activeTab === 1 ? 'Exchange Item' : 'Balance', 'Status', 'Action'].map(h => (
                  <TableCell key={h} sx={{ background: '#f8fafc', fontWeight: 700, fontSize: 11, color: T.blue, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', py: 1 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5, color: T.muted, fontSize: 13 }}>
                    No deliveries found.
                  </TableCell>
                </TableRow>
              ) : activeList.map((s, i) => {
                const bal = getBalance(s);

                return (
                  <TableRow
                    key={s.Id || i}
                    hover
                    sx={{ '&:hover': { background: '#f8fafc' }, cursor: 'pointer' }}
                    onClick={() => handleOpenDetail(s)}
                  >
                    <TableCell sx={{ fontSize: 11, color: T.muted }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, color: T.blue, whiteSpace: 'nowrap' }}>
                      {s.BillNo || `${s.BillPrefix || ''}${s.BillNumber || ''}`}
                    </TableCell>
                    {/* <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(s.CreatedDate || s.BillDate)}</TableCell> */}
                    <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(s.AllocatedDate || '-')}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.CustomerName || s.ContactNumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.textSec }}>
                      {s.ShipToAddress1 ? `${s.ShipToAddress1}${s.ShipToLocation ? ', ' + s.ShipToLocation : ''}` : (s.ShipTo || '-')}
                    </TableCell>
                    {/* <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{s.VehicleNumber || '-'}</TableCell> */}
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, textAlign: activeTab === 1 ? 'left' : 'right', color: activeTab === 1 ? T.text : (bal > 0 ? T.red : T.green), whiteSpace: 'nowrap' }}>
                      {activeTab === 1 ? (s.ExchangeItemName || '-') : fmt(bal)}
                    </TableCell>
                    {/* <TableCell>
                      <StatusBadge status={s.IsReceived ? 'Delivered' : (s._status || (activeTab === 0 ? 'COD' : 'OnSite'))} />
                    </TableCell> */}
                    <TableCell>
                      <StatusBadge
                        status={
                          s.IsReceived
                            ? (activeTab === 1 && s.IsExchangedCompleted ? 'Completed Pickup' : 'Delivered')
                            : (s._status || (activeTab === 0 ? 'COD' : 'OnSite'))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={e => { e.stopPropagation(); handleOpenDetail(s); }}
                        sx={{ fontSize: 10, py: 0.3, px: 1, borderRadius: 2 }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* ── Detail Dialog ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: `1px solid ${T.border}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
            🚚 Delivery Detail
          </Typography>
          <IconButton size="small" onClick={() => setDetailOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        {selectedSale && (
          <DialogContent sx={{ pt: 2 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              <StatusBadge status={selectedSale.IsReceived ? 'Delivered' : (selectedSale._status || (activeTab === 0 ? 'COD' : 'OnSite'))} />
              {(selectedSale._isCOD || activeTab === 0) && (
                <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#fce7f3', color: '#be185d' }}>
                  💵 COD: {fmt(getBalance(selectedSale))}
                </span>
              )}
            </div>

            {[
              ['Bill Number', selectedSale.BillNo || `${selectedSale.BillPrefix || ''}${selectedSale.BillNumber || ''}`],
              ['Bill Date', fmtDate(selectedSale.BillDate || selectedSale.CreatedDate)],
              ['Allocated Date', fmtDate(selectedSale.AllocatedDate)],
              ['Customer', selectedSale.CustomerName || '-'],
              ['Contact', selectedSale.CustomerContactNumber || '-'],
              ['Address', [selectedSale.ShipToAddress1, selectedSale.ShipToAddress2, selectedSale.ShipToLocation, selectedSale.ShipToPincode].filter(Boolean).join(', ') || '-'],
              ['Vehicle No', selectedSale.VehicleNumber || '-'],
              ...(activeTab === 0
                ? [
                  ['Grand Total', fmt(selectedSale.BillTotal)],
                  ['Balance / To Collect', fmt(getBalance(selectedSale))],
                ]
                : [
                  ['Exchange Item', selectedSale.ExchangeItemName || '-'],
                ]
              ),
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 600, minWidth: 130 }}>{label}</span>
                <span style={{ fontSize: 13, color: T.text, fontWeight: 500, textAlign: 'right', maxWidth: 250, wordBreak: 'break-word' }}>{value}</span>
              </div>
            ))}

            {/* ── COD Action Form ── */}
            {activeTab === 0 && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `2px dashed ${T.border}` }}>
                {/* Header + Amount Received inline */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.blue }}>💵 Payment Confirmation</Typography>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Checkbox checked={isReceived} onChange={e => setIsReceived(e.target.checked)} color="primary" size="small" sx={{ py: 0 }} />}
                    label={
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: isReceived ? T.green : T.text }}>
                        Received ({fmt(getBalance(selectedSale))})
                      </Typography>
                    }
                  />
                </Box>

                {/* Payment Mode — compact pill row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 1 }}>
                  {PAYMENT_MODES.map(pm => {
                    const sel = PaymentMode === pm.value;
                    return (
                      <Box
                        key={pm.value}
                        onClick={() => setPaymentType(pm.value)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.5,
                          px: 1.2, py: 0.5, borderRadius: 5,
                          border: `1.5px solid ${sel ? pm.color : T.border}`,
                          background: sel ? pm.bg : '#fafafa',
                          cursor: 'pointer', transition: 'all 0.12s', userSelect: 'none',
                          '&:hover': { borderColor: pm.color, background: pm.bg },
                        }}
                      >
                        <span style={{ fontSize: 13 }}>{pm.icon}</span>
                        <Typography sx={{ fontSize: 11, fontWeight: sel ? 700 : 500, color: sel ? pm.color : T.textSec, lineHeight: 1 }}>
                          {pm.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                {/* Remarks */}
                <TextField
                  fullWidth multiline rows={2} size="small"
                  label="Remarks" placeholder="Optional notes..."
                  value={driverRemarks} onChange={e => setDriverRemarks(e.target.value)}
                  inputProps={{ maxLength: 250, style: { fontSize: 11 } }}
                  helperText={
                    <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
                      <span>e.g. partial payment, customer request</span>
                      <span style={{ color: driverRemarks.length > 200 ? T.red : T.muted }}>{driverRemarks.length}/250</span>
                    </span>
                  }
                />
              </Box>
            )}

            {/* ── OnSite Action Form ── */}
            {activeTab === 1 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `2px dashed ${T.border}` }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.blue, mb: 1 }}>
                  PickUp Status
                </Typography>

                {/* Checkbox: Item Received */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isExchangedCompleted}
                      onChange={(e) => setIsExchangedCompleted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: isReceived ? T.green : T.text }}>
                      Item Received - {selectedSale.ExchangeItemName}
                    </Typography>
                  }
                />
                {/* Textfield: Remarks */}
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  label="Remarks"
                  placeholder="Add any notes about this pickup (optional)..."
                  value={driver1Remarks}
                  onChange={(e) => setDriver1Remarks(e.target.value)}
                  sx={{ mt: 1.5 }}
                  inputProps={{ maxLength: 250, style: { fontSize: 11 } }}
                />
              </Box>
            )}
          </DialogContent>
        )}

        <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setDetailOpen(false)}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2 }}
          >
            {selectedSale?.IsReceived ? 'Close' : 'Cancel'}
          </Button>

          {/* Already received — show Update if PaymentMode or Remarks changed */}
          {activeTab === 0 && selectedSale?.IsReceived && (
            (PaymentMode !== origPaymentMode || driverRemarks !== origDriverRemarks) && (
              <Button
                onClick={handleUpdateCOD}
                variant="contained"
                size="small"
                sx={{ borderRadius: 2, background: T.amber, color: '#fff', '&:hover': { background: '#b45309' } }}
              >
                Update
              </Button>
            )
          )}

          {/* Not yet received — normal Submit */}
          {activeTab === 0 && !selectedSale?.IsReceived && (
            <Button
              onClick={handleSubmitCOD}
              variant="contained"
              size="small"
              disabled={!isReceived}
              sx={{ borderRadius: 2, background: T.blue, color: '#fff' }}
            >
              Submit
            </Button>
          )}

          {/* Already completed pickup — show Update if Remarks changed */}
          {activeTab === 1 && selectedSale?.IsExchangedCompleted && (
            driver1Remarks !== origDriver1Remarks && (
              <Button
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    const payload = {
                      ...selectedSale,
                      CompanyId: companyId,
                      Id: selectedSale.Id,
                      IsExchangedCompleted: true,
                      Remarks: driver1Remarks,
                      ExchangeUpdatedBy: userId,
                    };
                    await updateData('UOMApi', selectedSale.Id, payload, token);
                    setOrigDriver1Remarks(driver1Remarks);
                    setSnack({ open: true, msg: 'Remarks updated successfully!', sev: 'success' });
                    setDetailOpen(false);
                    fetchAllData();
                  } catch {
                    setSnack({ open: true, msg: 'Failed to update remarks', sev: 'error' });
                  } finally {
                    setSubmitting(false);
                  }
                }}
                variant="contained"
                size="small"
                sx={{ borderRadius: 2, background: T.amber, color: '#fff', '&:hover': { background: '#b45309' } }}
              >
                Update
              </Button>
            )
          )}

          {/* Not yet completed — normal Submit */}
          {activeTab === 1 && !selectedSale?.IsExchangedCompleted && (
            <Button
              onClick={handleSubmitOnSite}
              variant="contained"
              size="small"
              disabled={!isExchangedCompleted}
              sx={{ borderRadius: 2, background: T.blue, color: '#fff' }}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(p => ({ ...p, open: false }))} sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverDashboard;